import React, { useMemo, useState } from "react";

// 6 neon flavors with glow colors + emoji + local-flavor taglines
const FLAVORS = [
  {
    id: "cherry-bomb",
    name: "Cherry Bomb",
    short: "Cherry",
    emoji: "🍒",
    color: "#FF2A2A",
    glow: "rgba(255, 42, 42, 0.5)",
    tagline: "The absolute classic. A local favorite after a tough shift.",
  },
  {
    id: "electric-lime",
    name: "Electric Lime",
    short: "Lime",
    emoji: "🍏",
    color: "#22C55E",
    glow: "rgba(34, 197, 94, 0.5)",
    tagline: "Sour, sweet, and packs a heavy pour punch.",
  },
  {
    id: "blue-razz",
    name: "Blue Razz",
    short: "Blue Razz",
    emoji: "🔷",
    color: "#3B82F6",
    glow: "rgba(59, 130, 246, 0.5)",
    tagline: "The pre-game fuel — perfect before catching the METRORail.",
  },
  {
    id: "sunny-yellow",
    name: "Sunny Yellow",
    short: "Sunny",
    emoji: "☀️",
    color: "#FFB800",
    glow: "rgba(255, 184, 0, 0.55)",
    tagline: "The house favorite. Named after the bar.",
  },
  {
    id: "grape-soda",
    name: "Grape Soda",
    short: "Grape",
    emoji: "🍇",
    color: "#A855F7",
    glow: "rgba(168, 85, 247, 0.5)",
    tagline: "Childhood nostalgia with a dive-bar kick.",
  },
  {
    id: "orange-crush",
    name: "Orange Crush",
    short: "Orange",
    emoji: "🍊",
    color: "#F97316",
    glow: "rgba(249, 115, 22, 0.55)",
    tagline: "Burnt orange. Hook 'em hangover edition.",
  },
];

// Resolves the live status line (no nested ternary)
const resolveStatus = ({ picked, total }) => {
  if (picked) return `Added ${picked.emoji} ${picked.name} to your imaginary tab!`;
  if (total > 0) return "Tab cleared. Ready for the next round!";
  return "Click a house-made flavor orb below to start pouring…";
};

const Header = () => (
  <div className="space-y-2">
    <h3
      data-testid="jello-heading"
      className="font-display text-3xl md:text-5xl uppercase tracking-wide text-white"
    >
      Build Your Tab:{" "}
      <span className="text-[#FF2A2A] neon-flicker">
        Famous $1 Jell-O Shots
      </span>
    </h3>
    <p className="text-[#A3A3A3] max-w-sm mx-auto text-xs md:text-sm">
      Tap the neon orbs below to calculate your running imaginary round totals.
    </p>
  </div>
);

const Scoreboard = ({ total, statusKey, statusText }) => (
  <div
    data-testid="jello-scoreboard"
    className="bg-[#161616] rounded-xl p-4 border-2 border-[#FF2A2A]/20 max-w-md mx-auto"
    style={{ boxShadow: "0 0 20px rgba(255, 42, 42, 0.05)" }}
  >
    <div className="grid grid-cols-2 gap-2 border-b border-[#262626] pb-3 mb-3 text-left font-mono-plex">
      <div>
        <span className="text-[10px] text-[#777] block uppercase font-bold tracking-[0.18em]">
          Total Orders
        </span>
        <span
          data-testid="jello-total-orders"
          className="font-display text-3xl md:text-4xl text-white tabular-nums"
        >
          {total} {total === 1 ? "Shot" : "Shots"}
        </span>
      </div>
      <div className="text-right">
        <span className="text-[10px] text-[#777] block uppercase font-bold tracking-[0.18em]">
          Running Damage
        </span>
        <span
          data-testid="jello-running-damage"
          className="font-display text-3xl md:text-4xl text-[#FFB800] tabular-nums"
        >
          ${total.toFixed(2)}
        </span>
      </div>
    </div>

    <div
      data-testid="jello-status-log"
      key={statusKey}
      className="text-xs italic text-[#A3A3A3] min-h-[32px] flex items-center justify-center px-2 rise"
    >
      {statusText}
    </div>
  </div>
);

const FlavorOrb = ({ flavor, onPick }) => (
  <button
    data-testid={`flavor-${flavor.id}`}
    onClick={() => onPick(flavor)}
    className="flex flex-col items-center space-y-2 group"
  >
    <span
      className="w-12 h-12 md:w-14 md:h-14 rounded-full border flex items-center justify-center text-xl md:text-2xl group-hover:scale-110 transition-all duration-150"
      style={{
        background: `${flavor.color}22`,
        borderColor: flavor.color,
        boxShadow: `0 0 10px ${flavor.glow}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 22px ${flavor.glow}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = `0 0 10px ${flavor.glow}`;
      }}
    >
      {flavor.emoji}
    </span>
    <span className="text-[10px] font-mono-plex tracking-tight text-[#A3A3A3] group-hover:text-white">
      {flavor.short}
    </span>
  </button>
);

const FlavorGrid = ({ onPick }) => (
  <div
    data-testid="jello-orb-grid"
    className="grid grid-cols-3 sm:grid-cols-6 gap-3 max-w-xl mx-auto pt-2"
  >
    {FLAVORS.map((f) => (
      <FlavorOrb key={f.id} flavor={f} onPick={onPick} />
    ))}
  </div>
);

const ResetButton = ({ disabled, onClick }) => (
  <button
    onClick={onClick}
    data-testid="jello-reset"
    disabled={disabled}
    className="text-xs uppercase font-mono-plex tracking-[0.25em] text-[#777] hover:text-[#FF2A2A] disabled:opacity-40 disabled:cursor-not-allowed transition pt-2"
  >
    [ Clear Tab Reset ]
  </button>
);

// Pure module-level reducer — keeps the useMemo body dependency-free
const sumTabValues = (tab) => {
  return Object.values(tab).reduce((sum, count) => sum + count, 0);
};

const JelloShotPicker = () => {
  const [picked, setPicked] = useState(null);
  const [tab, setTab] = useState({});

  const total = useMemo(() => sumTabValues(tab), [tab]);

  const handlePick = (flavor) => {
    setPicked(flavor);
    setTab((prev) => ({ ...prev, [flavor.id]: (prev[flavor.id] || 0) + 1 }));
  };

  const reset = () => {
    setTab({});
    setPicked(null);
  };

  const statusText = resolveStatus({ picked, total });
  const statusKey = picked?.id || `reset-${total}`;

  return (
    <section
      id="jello"
      data-testid="jello-shot-picker"
      className="bg-[#0F0F0F] text-white py-16 md:py-20 px-4 border-t border-[#222] text-center"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <Header />
        <Scoreboard
          total={total}
          statusKey={statusKey}
          statusText={statusText}
        />
        <FlavorGrid onPick={handlePick} />
        <ResetButton disabled={total === 0} onClick={reset} />
      </div>
    </section>
  );
};

export default JelloShotPicker;
